import { Link } from "@inertiajs/react"
import type { ReactNode } from "react"
import { cn } from "@/lib/utils"

/** Иконки плиток — одного размера и тона, как в заголовках биллинга. */
export const STAT_TILE_ICON_CLASS = "size-3.5 shrink-0 text-ui-text-muted"

export const STAT_TILE_CLASS = "block min-w-0 rounded-lg border border-ui-border bg-ui-bg-elevated px-4 py-3 text-left"

export const STAT_TILE_INTERACTIVE_CLASS =
  "group/tile cursor-pointer transition-colors hover:border-ui-border-strong hover:bg-ui-surface-raised focus-visible:ring-2 focus-visible:ring-ui-accent/40 focus-visible:outline-none"

export type StatTileContentProps = {
  icon: ReactNode
  label: ReactNode
  value: string
  sub?: ReactNode
  /** Цвет значения, когда состояние важнее нейтрального: минус на счёте, ноль владельцев. */
  valueClassName?: string
  /** Графика справа от значения: полоса доли, стопка аватаров. */
  aside?: ReactNode
  /** Маркер в правом верхнем углу: стрелка перехода или шеврон меню. */
  trailing?: ReactNode
}

/** Только строчные элементы: плитка бывает и ссылкой, и кнопкой меню. */
export function StatTileContent({ icon, label, value, sub, valueClassName, aside, trailing }: StatTileContentProps) {
  return (
    <>
      <span className="flex items-center gap-1.5 text-[10px] font-semibold tracking-wide text-ui-text-secondary uppercase">
        {icon}
        <span className="min-w-0 flex-1 truncate">{label}</span>
        {trailing ? (
          <span className="shrink-0 text-ui-text-muted transition-colors group-hover/tile:text-ui-text-primary">
            {trailing}
          </span>
        ) : null}
      </span>
      <span className="mt-2 flex min-w-0 items-center justify-between gap-3">
        <span
          className={cn(
            "truncate text-lg leading-6 font-semibold tracking-tight tabular-nums",
            valueClassName ?? "text-ui-text-primary",
          )}
          title={value}
        >
          {value}
        </span>
        {aside}
      </span>
      <span className="mt-0.5 block truncate text-[11px] leading-4 text-ui-text-muted">{sub ?? " "}</span>
    </>
  )
}

export function StatTile({ className, ...content }: StatTileContentProps & { className?: string }) {
  return (
    <div className={cn(STAT_TILE_CLASS, className)}>
      <StatTileContent {...content} />
    </div>
  )
}

export function StatTileLink({
  href,
  title,
  className,
  ...content
}: StatTileContentProps & { href: string; title?: string; className?: string }) {
  return (
    <Link href={href} title={title} className={cn(STAT_TILE_CLASS, STAT_TILE_INTERACTIVE_CLASS, className)}>
      <StatTileContent {...content} />
    </Link>
  )
}
